// src/modules/app-views/products/pages/Document/modals/ProductsParametersModal/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useCallback, useEffect } from 'react';
import { Modal, Tabs } from 'antd';
import { SettingOutlined } from '@ant-design/icons';
import translateX from 'utils/translateX';
import appService from 'services/appService';

// Tabs
import WarehousesTab from './tabs/WarehousesTab';
import UnitsTab from './tabs/UnitsTab';
import BrandsTab from './tabs/BrandsTab';
import CategoriesTab from './tabs/CategoriesTab';
import TagsTab from './tabs/TagsTab';
import SkuTab from './tabs/SkuTab';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const ProductsParametersModal = ({ show, setShow, afterClose, initialTab, resetInitialTab }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const defaultTab = 'units';

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState(initialTab || defaultTab);
  const [loading, setLoading] = useState(false);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  // When modal opens with initialTab, navigate to that tab
  useEffect(() => {
    if (show && initialTab) {
      setActiveTab(initialTab);
    }
  }, [show, initialTab]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleClose = useCallback(() => {
    if (!loading) {
      setShow(false);
    }
  }, [loading, setShow]);

  const handleAfterClose = useCallback(() => {
    setActiveTab(defaultTab);
    setLoading(false);
    resetInitialTab?.();
    afterClose?.();
  }, [afterClose, resetInitialTab, defaultTab]);

  const handleTabChange = useCallback(key => {
    setActiveTab(key);
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const tabItems = [
    {
      key: 'units',
      label: translateX('units'),
      children: <UnitsTab loading={loading} setLoading={setLoading} />,
    },
    {
      key: 'brands',
      label: translateX('brands'),
      children: <BrandsTab loading={loading} setLoading={setLoading} />,
    },
    {
      key: 'categories',
      label: translateX('categories'),
      children: <CategoriesTab loading={loading} setLoading={setLoading} />,
    },
    {
      key: 'tags',
      label: translateX('tags'),
      children: <TagsTab loading={loading} setLoading={setLoading} />,
    },
    {
      key: 'warehouses',
      label: translateX('warehouses'),
      children: <WarehousesTab loading={loading} setLoading={setLoading} />,
    },
    {
      key: 'sku',
      label: 'SKU',
      children: <SkuTab loading={loading} setLoading={setLoading} />,
    },
  ];

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Modal
      title={
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <SettingOutlined />
          {translateX('parameters')}
        </span>
      }
      open={show}
      onCancel={handleClose}
      afterClose={handleAfterClose}
      destroyOnHidden
      width={480}
      footer={null}
      mask={{ closable: !loading }}
      styles={{
        body: {
          maxHeight: 'calc(80vh - 110px)', // 80vh minus header and padding
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        },
      }}
      style={{ top: '10vh' }}
    >
      <Tabs
        activeKey={activeTab}
        onChange={handleTabChange}
        items={tabItems}
        style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
        className="parameters-modal-tabs"
      />
      <style>{`
        .parameters-modal-tabs .ant-tabs-content-holder {
          flex: 1;
          overflow: hidden;
        }
        .parameters-modal-tabs .ant-tabs-content {
          height: 100%;
        }
        .parameters-modal-tabs .ant-tabs-tabpane {
          height: 100%;
          overflow: hidden;
        }
      `}</style>
    </Modal>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default ProductsParametersModal;
